// PJL-3.1 — human uploader names for Processing surfaces (owner UX
// 2026-07-18). Internal IDs like "auth0|68f1…" mean nothing to an operator, so
// list/table surfaces resolve them against the employee directory and only the
// Job Detail (debug) fields keep the raw Auth0 subject.
//
// Owner fallback order: 1. Employee Name → 2. User Display Name →
// 3. Email Address → 4. Auth0 Subject (last resort only).

import { useMemo } from "react";

import { useListEmployeesQuery } from "@/features/employees/employeesApi";
import type { ApiEmployee } from "@/features/employees/employeeTypes";

export interface UploaderLabel {
  /** Text to render. */
  label: string;
  /** True when this is a human name/email; false for a raw ID fallback. */
  resolved: boolean;
}

/** Owner fallback chain applied to one employee-directory record. */
export function employeeDisplayName(employee: ApiEmployee): string | null {
  const fullName = [employee.firstName, employee.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  if (fullName) return fullName;
  if (employee.displayName) return employee.displayName;
  if (employee.emailAddress) return employee.emailAddress;
  return null;
}

export interface UploaderDirectory {
  /** Resolve an actor to a display label (raw ID only as last resort). */
  resolve: (
    sub?: string | null,
    employeeId?: string | null,
  ) => UploaderLabel;
  /** Directory entries with a resolvable name (for filter dropdowns). */
  options: { label: string; value: string }[];
  /** True once the directory query has settled (loaded or failed). */
  ready: boolean;
}

/**
 * Loads the employee directory once (RTK-cached) and returns a resolver.
 * If the directory is unavailable (e.g. the viewer lacks access), every
 * lookup degrades gracefully to the raw ID fallback.
 */
export function useUploaderDirectory(): UploaderDirectory {
  const { data, isError, isLoading } = useListEmployeesQuery();

  return useMemo(() => {
    const bySub = new Map<string, string>();
    const byEmployeeId = new Map<string, string>();
    const options: { label: string; value: string }[] = [];
    for (const employee of data?.employees ?? []) {
      const name = employeeDisplayName(employee);
      if (!name) continue;
      if (employee.auth0Sub) {
        bySub.set(employee.auth0Sub, name);
        options.push({ label: name, value: employee.auth0Sub });
      }
      if (employee.employeeId) byEmployeeId.set(employee.employeeId, name);
    }
    options.sort((a, b) => a.label.localeCompare(b.label));

    const resolve = (
      sub?: string | null,
      employeeId?: string | null,
    ): UploaderLabel => {
      if (sub && bySub.has(sub)) {
        return { label: bySub.get(sub) as string, resolved: true };
      }
      if (employeeId && byEmployeeId.has(employeeId)) {
        return { label: byEmployeeId.get(employeeId) as string, resolved: true };
      }
      // Last resort — raw internal ID (owner: acceptable only as fallback).
      const raw = sub || employeeId;
      return raw ? { label: raw, resolved: false } : { label: "—", resolved: false };
    };

    return { resolve, options, ready: !isLoading && (!!data || isError) };
  }, [data, isError, isLoading]);
}
